import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Bell, ArrowRight, CheckCircle2 } from 'lucide-react';
import type { ApprovalStage } from '@/types/approvalWorkflow';

interface CompactStageCardProps {
  stage: ApprovalStage;
  stageNumber: number;
  isLastStage: boolean;
  onEdit: (stage: ApprovalStage) => void;
}

export function CompactStageCard({ stage, stageNumber, isLastStage, onEdit }: CompactStageCardProps) {
  const hasApprovers = stage.approvers && stage.approvers.length > 0;
  // Check both explicit rules array and routing configuration (onApproved/onRejected/timeout)
  const hasRules = (stage.rules && stage.rules.length > 0) || stage.routingConfigured;
  const hasNotifications = stage.notificationSettings?.enabled;
  
  // Determine if stage is fully configured (approvers are required, rules are optional but shown)
  const isConfigured = hasApprovers;

  return (
    <div className="relative">
      <Card 
        className="p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
        onClick={() => onEdit(stage)}
      >
        <div className="flex items-start gap-4">
          {/* Stage Number Badge with Gradient */}
          <div className="flex-shrink-0 relative">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground flex items-center justify-center font-bold text-lg shadow-md">
              {stageNumber}
            </div>
            {/* Status Indicator */}
            {isConfigured ? (
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 border-2 border-background flex items-center justify-center">
                <CheckCircle2 className="h-3 w-3 text-white" />
              </div>
            ) : (
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 border-2 border-background flex items-center justify-center">
                <span className="text-white text-xs font-bold">!</span>
              </div>
            )}
          </div>

          {/* Stage Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="mb-3">
              <h3 className="font-semibold text-lg mb-1 truncate">{stage.name}</h3>
              {stage.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">{stage.description}</p>
              )}
            </div>

            {/* Stage Details - Colored Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Approvers Card - Blue */}
              <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 transition-all hover:shadow-md">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-md bg-blue-500/10">
                    <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-xs font-medium text-blue-900 dark:text-blue-100">Approvers</span>
                </div>
                {hasApprovers ? (
                  <div>
                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-1">
                      {stage.approvers.length}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {stage.approvers.slice(0, 2).map((approver, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700">
                          {approver.name || approver.email}
                        </Badge>
                      ))}
                      {stage.approvers.length > 2 && (
                        <Badge variant="secondary" className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700">
                          +{stage.approvers.length - 2}
                        </Badge>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-blue-600/60 dark:text-blue-400/60">Not configured</div>
                )}
              </div>

              {/* Rules Card - Amber */}
              <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 transition-all hover:shadow-md">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-md bg-amber-500/10">
                    <CheckCircle2 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <span className="text-xs font-medium text-amber-900 dark:text-amber-100">Rules</span>
                </div>
                {hasRules ? (
                  <div>
                    <div className="text-2xl font-bold text-amber-700 dark:text-amber-300 mb-1">
                      {stage.rules?.length || 0}
                    </div>
                    <div className="text-xs text-amber-600 dark:text-amber-400">
                      {(stage.rules?.length || 0) === 1 ? 'condition' : 'conditions'}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-amber-600/60 dark:text-amber-400/60">Not configured</div>
                )}
              </div>

              {/* Notifications Card - Green */}
              <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20 border border-green-200 dark:border-green-800 p-3 transition-all hover:shadow-md">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-md bg-green-500/10">
                    <Bell className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="text-xs font-medium text-green-900 dark:text-green-100">Notifications</span>
                </div>
                {hasNotifications ? (
                  <div>
                    <div className="text-2xl font-bold text-green-700 dark:text-green-300 mb-1">
                      ✓
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">
                      Enabled
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-green-600/60 dark:text-green-400/60">Disabled</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Flow Indicator */}
      {!isLastStage && (
        <div className="flex justify-center py-2">
          <ArrowRight className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
